import { memo, useCallback, useMemo, useState } from 'react';
import { format } from 'date-fns';

import { Box, Paragraph, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertySpecialOfferV2WebApiHandler_delete } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useFeedOverrideSectionEnabled } from '../../../../../hooks/useFeedOverrideSectionEnabled';
import { usePropertyEditMutation } from '../../../../../hooks/usePropertyEditMutation';
import SpecialOffersModal from '../../../../modals/SpecialOffersModal';

import type { UserPropertyDetailsSpecialOfferDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  index: number;
  specialOffer: UserPropertyDetailsSpecialOfferDto;
  isInputFormDisabled: boolean;
  listingAlias: string;
};

// Offers added in Rental Manager are a zram supplement on a feed listing and a rental manager offer on a manual one.
const EDITABLE_SOURCE_TYPES = new Set(['zramSupplement', 'rentalManager']);
// Reps add offers as feed supplements, and so did Rental Manager before this listing's offers moved onto feed
// overrides, so an offer of this source is only read only once the override is what Rental Manager writes.
const SOURCE_TYPE_EDITABLE_WITHOUT_OVERRIDES = 'feedSupplement';

const SavedSpecialOfferCard = ({ index, specialOffer, isInputFormDisabled, listingAlias }: Props) => {
  const { enqueueToast } = useToast();

  const [showSpecialOffersModal, setShowSpecialOffersModal] = useState(false);

  const { startDateYyyyMmDd: startDate, endDateYyyyMmDd: endDate, description, offerIdEncoded } = specialOffer;
  const isSpecialOffersOverrideEnabled = useFeedOverrideSectionEnabled(
    listingAlias,
    FeedOverrideSection.SPECIAL_OFFERS,
  );
  const isEditableOffer =
    EDITABLE_SOURCE_TYPES.has(specialOffer.sourceType) ||
    (specialOffer.sourceType === SOURCE_TYPE_EDITABLE_WITHOUT_OVERRIDES && !isSpecialOffersOverrideEnabled);

  const formattedStartDate = useMemo(
    () => (startDate ? format(new Date(`${startDate}T10:00:00`), 'MM/dd/yyyy') : ' - '),
    [startDate],
  );
  const formattedEndDate = useMemo(
    () => (endDate ? format(new Date(`${endDate}T10:00:00`), 'MM/dd/yyyy') : ' - '),
    [endDate],
  );

  const { mutateAsync: deleteOffer, isPending: isDeleting } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertySpecialOfferV2WebApiHandler_delete,
    successToastMessage: 'Special offer deleted',
    meta: {
      context: {
        api: 'RentalManagerUserPropertySpecialOfferV2WebApiHandler_delete',
        details: { listingAlias, offerIdEncoded },
      },
    },
    onError: () => {
      enqueueToast(<Toast tone="critical">Special offer couldn’t be deleted. Try again.</Toast>);
    },
  });

  const handleEditClick = useCallback(() => setShowSpecialOffersModal(true), []);

  const handleDeleteClick = useCallback(
    () =>
      deleteOffer({
        postData: {
          listingAlias,
          offerIdEncoded: offerIdEncoded || '',
        },
      }),
    [deleteOffer, listingAlias, offerIdEncoded],
  );

  const renderOfferDateDisplayText = useCallback(
    ({
      startDate,
      endDate,
      formattedStartDate,
      formattedEndDate,
    }: {
      startDate: string;
      endDate: string;
      formattedStartDate: string;
      formattedEndDate: string;
    }) => {
      let offerDateDisplayText = '';

      switch (true) {
        case Boolean(!startDate && !endDate):
          offerDateDisplayText = 'Ongoing';
          break;
        case Boolean(!startDate && endDate):
          offerDateDisplayText = `Until ${formattedEndDate}`;
          break;
        case Boolean(startDate && !endDate):
          offerDateDisplayText = `${formattedStartDate} - ongoing`;
          break;
        default:
          offerDateDisplayText = `${formattedStartDate}–${formattedEndDate}`;
          break;
      }

      return <Text textStyle="body-bold">{offerDateDisplayText}</Text>;
    },
    [],
  );

  return (
    <div data-testid={`SavedSpecialOfferCard-${index}`}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'row',
            gap: '200',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          {renderOfferDateDisplayText({ startDate, endDate, formattedStartDate, formattedEndDate })}

          {!isInputFormDisabled && isEditableOffer && (
            <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
              <TextButton onClick={handleEditClick}>Edit</TextButton>
              <TextButton
                onClick={handleDeleteClick}
                data-testid="delete-button"
                disabled={!offerIdEncoded || isDeleting}
              >
                Delete
              </TextButton>
            </Box>
          )}
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
          <Paragraph>{description}</Paragraph>
        </Box>
      </Box>

      {showSpecialOffersModal && (
        <SpecialOffersModal
          isOpen={showSpecialOffersModal}
          onClose={() => setShowSpecialOffersModal(false)}
          defaultOffer={specialOffer}
        />
      )}
    </div>
  );
};

export default memo(SavedSpecialOfferCard);
